/************************************************************************
 ** IBM Confidential
 **
 ** IBM Business Analytics: Rapidly Adaptive Visualization Engine
 **
 ** (C) Copyright IBM Corp. 2024
 **
 ** The source code for this program is not published or otherwise divested of its trade secrets, 
 ** irrespective of what has been deposited with the U.S. Copyright Office.
 ************************************************************************/
// GENERATED
import BaseMoserObject from './BaseMoserObject';
import QsClassifierType from './QsClassifierType';
import Operator from './Operator';
import InstanceType from './InstanceType';
import QuerySubjectUsage from './QuerySubjectUsage';
import MoserObject from './MoserObject';
import ShortcutUtils from './ShortcutUtils';
import UseSpecType from './UseSpecType';
import QuerySubject from './QuerySubject';
import SetOperation from './SetOperation';
//@import BaseMoserObject (loadtime) // superclass
//@import com/ibm/bi/platform/moser/common/generated/metadata/QsClassifierType (runtime) // QsClassifierType
//@import com/ibm/bi/platform/moser/client/queryFlow/Operator (runtime) // Operator
//@import com/ibm/bi/platform/moser/common/generated/metadata/InstanceType (runtime) // InstanceType
//@import com/ibm/bi/platform/moser/common/generated/metadata/QuerySubjectUsage (runtime) // QuerySubjectUsage
//@import com/ibm/bi/platform/moser/common/generated/metadata/MoserObject (runtime) // getModuleObject
//@import com/ibm/bi/platform/moser/common/utils/ShortcutUtils (runtime) // isShortcutOrAlias, isShortcutOrAliasValid
//@import com/ibm/bi/platform/moser/common/generated/metadata/UseSpecType (runtime) // UseSpecType
//@import com/ibm/bi/platform/moser/common/generated/metadata/QuerySubject (runtime) // new

/**
 * @author  NataliaBalaba
 */
export default class QueryFlowUtil extends BaseMoserObject{

	/**
	 * This method is helping to clean up a mess MUI created depicted in 293282
	 * @param {QuerySubject} querysubject
	 * @return {QsClassifierType}
	 */
	_getClassifier(querysubject) {
		if (querysubject.getClassifier()) {
			return querysubject.getClassifier();
		}
		return QsClassifierType.QUERY_SUBJECT;
	}

	/**
	 * Return type of query subject
	 * @param {QuerySubject} querysubject - query subject
	 * @return {Operator}  type of query subject
	 */
	getQuerySubjectType(querysubject) {
		if (!querysubject) {
			return null;
		}
		switch (this._getClassifier(querysubject)) {
		case QsClassifierType.JOIN_QUERY_SUBJECT:
			return Operator.JOIN;
		case QsClassifierType.OPERATION_QUERY_SUBJECT:
			var operation = querysubject.getQueryOperation().getSetOperation();
			switch (operation) {
			case SetOperation.EXCEPT:
				return Operator.EXCEPT;
			case SetOperation.INTERSECT:
				return Operator.INTERSECT;
			case SetOperation.UNION:
				return Operator.UNION;
			default:
			}
		case QsClassifierType.SQL_QUERY_SUBJECT:
			return Operator.SQL;
		case QsClassifierType.METRIC_DEFINITION:
			return Operator.METRIC_DEFINITION;
		case QsClassifierType.METRIC_INSTANCE:
			return Operator.METRIC_INSTANCE;
		default:
			return querysubject.getInstanceType() == InstanceType.REFERENCE || this.getPrecursors(querysubject).length==0 ? Operator.PLAIN : Operator.VIEW;
		}
	}

	/**
	 * Returns collection of selectable filters (if any) for the query subject
	 * @param {QuerySubject} querysubject - a query subject to compute selectable filters
	 * @return {Array}  collection of selectable filters (if any) for the query subject
	 */
	getSelectableFilters(querysubject) {
		var filters = [];
		this._appendFilters(querysubject.getItem(), filters);
		return filters;
	}

	_appendFilters(items, filters) {
		for (var __i_enFor0 = 0, __exp_enFor0 = items, __len_enFor0 = __exp_enFor0.length;
				__i_enFor0 < __len_enFor0; ++__i_enFor0) {
			var item = __exp_enFor0[__i_enFor0];
			if (item.getFilter()) {
				filters.push(item.getFilter());
			} else if (item.getFolder()) {
				this._appendFilters(item.getFolder().getItem(), filters);
			}
		}
	}

	/**
	 * Extract pre and post aggregate filters and place them in the respective collections
	 * @param {QuerySubject} querysubject - a query subject with the filters
	 * @param  preAggregate - empty list of pre aggregate filters to be filled with filters
	 * @param  postAggregate - empty list of post aggregate filters to be filled with filters
	 */
	extractFilters(querysubject) {
		var preAggregate = [], postAggregate = [];
		if (querysubject.basicGetFilter()) {
			for (var __i_enFor0 = 0, __exp_enFor0 = querysubject.getFilter(), __len_enFor0 = __exp_enFor0.length;
					__i_enFor0 < __len_enFor0; ++__i_enFor0) {
				var filter = __exp_enFor0[__i_enFor0];
				if (filter.isPostAutoAggregation() != null && filter.isPostAutoAggregation() == true) {
					postAggregate.push(filter);
				} else {
					preAggregate.push(filter);
				}
			}
		}
		var result = [];
		result[0] = preAggregate;
		result[1] = postAggregate;
		return result;
	}

	/**
	 * @param {QuerySubject} querysubject
	 * @return {boolean}
	 */
	hasAggregate(querysubject) {
		return querysubject.getQuerySubjectUsage() == QuerySubjectUsage.SUMMARY_QUERY;
	}

	/**
	 * Returns the query subjects that this query subject is based on - e.g. union legs, join participants, etc. The returned query subjects are in the current data module or in a package this data module references. If a query subject is based on any other external reference. The external reference is not returned.
	 * @param {QuerySubject} querysubject
	 * @return {Array}
	 */
	getPrecursors(querysubject) {
		var module = MoserObject.getModuleObject(querysubject);
		if (!module) {
			return [];
		}
		switch (this._getClassifier(querysubject)) {
		case QsClassifierType.JOIN_QUERY_SUBJECT:
			if (ShortcutUtils.isShortcutOrAliasValid(querysubject)) {
				return this._fromReference(querysubject.getOriginalRef(), module);
			}
			return this._fromReference(querysubject.getJoinOperation().getQueryRef(), module);
		case QsClassifierType.OPERATION_QUERY_SUBJECT:
			if (ShortcutUtils.isShortcutOrAliasValid(querysubject)) {
				return this._fromReference(querysubject.getOriginalRef(), module);
			}
			return this._fromReference(querysubject.getQueryOperation().getQueryRef(), module);
		default:
			var instanceType = this._getInstanceType(querysubject);
			switch (instanceType) {
			case InstanceType.MISSING:
				if (ShortcutUtils.isShortcutOrAlias(querysubject, true)) {
					return this._fromReference(querysubject.getRef(), module);
				}
				return [];
			case InstanceType.REFERENCE:
				if (ShortcutUtils.isShortcutOrAliasValid(querysubject)) {
					return this._fromReference(querysubject.getOriginalRef(), module);
				}
				return [];
			default:
				return this._fromReference(querysubject.getRef(), module);
			}
		}
	}

	/**
	 * Returns an object structure to represent table dependencies in a module. Maps a query subject identifier to a list of query subjects the identifier references. An identifier not present in the structure represents the query subject references across data modules. An identifier present but maps to an empty list represents the query subject is part of a circular reference. If the moserObject is a Module than it will return the table dependencies of the whole module. If the moserObject is a QuerySubject than it will only return the table dependencies for the query subject.
	 * @param {MoserObject} moserObject  Module or QuerySubject
	 * @return {Map}  Map<String, List<QuerySubject>>
	 */
	getInternalModuleTableDependencies(moserObject) {
		if (!moserObject) {
			return null;
		}
		if (!("Module" == moserObject.getObjectType()) && !("QuerySubject" == moserObject.getObjectType())) {
			return null;
		}
		var scopeQS;
		var module;
		if ("QuerySubject" == moserObject.getObjectType()) {
			scopeQS = moserObject;
			module = MoserObject.getModuleObject(moserObject);
		}
		if ("Module" == moserObject.getObjectType()) {
			module = moserObject;
		}
		if (!module) {
			return null;
		}
		if (scopeQS) {
			return this._getQSTableDep(scopeQS);
		}
		return this._getModuleTableDep(module);
	}

	/**
	 * Returns an object structure to represent table dependencies in a module.
	 * @param {Module} module
	 * @return {Map}
	 */
	_getModuleTableDep(module) {
		var seenAllQSSet = {};
		var tableDepMap = {};
		var qsList = module.basicGetQuerySubject();
		if (!qsList || qsList.length==0) {
			return null;
		}
		for (var __i_enFor0 = 0, __exp_enFor0 = qsList, __len_enFor0 = __exp_enFor0.length;
				__i_enFor0 < __len_enFor0; ++__i_enFor0) {
			var qs = __exp_enFor0[__i_enFor0];
			if (seenAllQSSet.hasOwnProperty(qs.getIdentifier())) {
				continue;
			}
			var stack = [];
			stack.push(qs);
			var visitedQSSet = {};
			visitedQSSet[qs.getIdentifier()] = 1;
			var visitedQSList = [];
			visitedQSList.push(qs.getIdentifier());
			this._getQSTableDepImp(qs, this.getPrecursors(qs), tableDepMap, stack, visitedQSSet, seenAllQSSet);
		}
		return tableDepMap;
	}

	/**
	 * Return a object structure to represent table dependencies in a query subject.
	 * @param {QuerySubject} qs
	 * @return {Map}
	 */
	_getQSTableDep(qs) {
		var tableDepMap = {};
		var stack = [];
		var visitedSet = {};
		var visitedList = [];
		var refQSList = this.getPrecursors(qs);
		if (refQSList.length==0) {
			return null;
		}
		stack.push(qs);
		visitedSet[qs.getIdentifier()] = 1;
		visitedList.push(qs.getIdentifier());
		this._getQSTableDepImp(qs, refQSList, tableDepMap, stack, visitedSet, {});
		return tableDepMap;
	}

	/**
	 * Return a object structure to represent table dependencies in a query subject.
	 * @param {QuerySubject} qs            current query subject.
	 * @param {Array} qsRefList     precursor of qs.
	 * @param {Map} tableDepMap   represents the table dependencies.
	 * @param {Array} stack         stores chain of visited query subjects.
	 * @param {Set} visitedSet    stores a set of visited query subject identifiers in the path.
	 * @param {Set} seenAllQSSet  stores a set of all visited query subject identifiers.
	 */
	_getQSTableDepImp(qs, qsRefList, tableDepMap, stack, visitedSet, seenAllQSSet) {
		seenAllQSSet[qs.getIdentifier()] = 1;
		if (qsRefList.length==0) {
			return;
		}
		var qsDep = tableDepMap[qs.getIdentifier()];
		if (qsDep) {
			return;
		}
		qsDep = [];
		tableDepMap[qs.getIdentifier()] = qsDep;
		Array.prototype.push.apply(qsDep, qsRefList);
		for (var __i_enFor0 = 0, __exp_enFor0 = qsRefList, __len_enFor0 = __exp_enFor0.length;
				__i_enFor0 < __len_enFor0; ++__i_enFor0) {
			var ref = __exp_enFor0[__i_enFor0];
			var refTableDep = tableDepMap[ref.getIdentifier()];
			if (visitedSet.hasOwnProperty(ref.getIdentifier()) || refTableDep && refTableDep.length==0) {
				QueryFlowUtil._circularTableDep(stack, tableDepMap);
			} else if (!refTableDep) {
				stack.push(ref);
				var newVisitedSet = {};
				for (var __i_enFor1 = 0, __exp_enFor1 = stack, __len_enFor1 = __exp_enFor1.length;
						__i_enFor1 < __len_enFor1; ++__i_enFor1) {
					var stackQS = __exp_enFor1[__i_enFor1];
					newVisitedSet[stackQS.getIdentifier()] = 1;
				}
				this._getQSTableDepImp(ref, this.getPrecursors(ref), tableDepMap, stack, newVisitedSet, seenAllQSSet);
				stack.splice(stack.length - 1, 1);
			}
		}
	}

	/**
	 * Populates the table dependencies of query subjects in a circular reference.
	 * @param {Array} stack        stores chain of visited query subjects.
	 * @param {Map} tableDepMap  represents the table dependencies.
	 */
	static _circularTableDep(stack, tableDepMap) {
		for (var __i_enFor0 = 0, __exp_enFor0 = stack, __len_enFor0 = __exp_enFor0.length;
				__i_enFor0 < __len_enFor0; ++__i_enFor0) {
			var qs = __exp_enFor0[__i_enFor0];
			var qsDep = tableDepMap[qs.getIdentifier()];
			if (!(qsDep.length==0)) {
				qsDep.length = 0;
			}
		}
	}

	_getInstanceType(querysubject) {
		if (!querysubject.getInstanceType()) {
			return InstanceType.COPY;
		}
		return querysubject.getInstanceType();
	}

	/**
	 * Returns the references to query subjects in the current data module or a referenced package
	 * @param {Array} references
	 * @param {Module} module
	 */
	_fromReference(references, module) {
		var tables = [];
		for (var __i_enFor0 = 0, __exp_enFor0 = references, __len_enFor0 = __exp_enFor0.length;
				__i_enFor0 < __len_enFor0; ++__i_enFor0) {
			var ref = __exp_enFor0[__i_enFor0];
			var keep = false;
			var parts = ref.split(new RegExp("\\."));
			if (parts.length == 1) {
				keep = true;
			} else {
				var id = parts[0];
				for (var __i_enFor1 = 0, __exp_enFor1 = module.getUseSpec(), __len_enFor1 = __exp_enFor1.length;
						__i_enFor1 < __len_enFor1; ++__i_enFor1) {
					var useSpec = __exp_enFor1[__i_enFor1];
					if (id == useSpec.getIdentifier() && useSpec.getType() == UseSpecType.PACKAGE) {
						keep = true;
						break;
					}
				}
			}
			if (keep) {
				var qs = this._getQuerySubjectById(ref, module);
				if (!qs) {
					qs = new QuerySubject();
					qs.setInstanceType(InstanceType.MISSING);
					qs.setIdentifier(ref);
				}
				tables.push(qs);
			}
		}
		return tables;
	}

	_getQuerySubjectById(id, module) {
		var querySubject;
		var qsList = module.basicGetQuerySubject();
		if (qsList) {
			for (var __i_enFor0 = 0, __exp_enFor0 = qsList, __len_enFor0 = __exp_enFor0.length;
					__i_enFor0 < __len_enFor0; ++__i_enFor0) {
				var qs = __exp_enFor0[__i_enFor0];
				if (qs.getIdentifier() == id) {
					querySubject = qs;
					break;
				}
			}
		}
		return querySubject;
	}
}


// WEBPACK FOOTER //
// ./src/generated/QueryFlowUtil.js